// Import Angular and related items
import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
 
// Import services
import { AuthService, LoginResult } from '@core/services/auth.service';
 
/**
 * Tracks an in-progress token refresh, shared across all requests.
 *
 * When several requests fail with a 401 at the same time, they all wait on this
 * single refresh instead of each starting their own. It is cleared once the
 * refresh finishes so the next expiry can start a fresh one.
 */
let refreshInFlight$: Observable<LoginResult> | null = null;
 
/** Returns a copy of the request with the current auth token attached. */
const addHeaders = (request: HttpRequest<any>, authService: AuthService): HttpRequest<any> => {
  return request.clone({
    headers: request.headers.set('Authorization', `Bearer ${authService.Token}`)
  });
};
 
export const AuthorizationInterceptorFunctional: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
 
  // Attach the token to outgoing requests, except for static assets.
  if (authService.Token != null && !req.url.startsWith('assets/')) {
    req = addHeaders(req, authService);
  }
 
  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // Requests to the auth endpoints don't get a retry. A failed refresh
      // means the session can't be recovered, so sign the user out; a failed
      // login just surfaces its own error to the login screen.
      if (req.url.startsWith(authService.rootUrl)) {
        if (req.url.startsWith(`${authService.rootUrl}/refresh`)) {
          authService.logout();
        }
        return throwError(err);
      }
 
      // Anything that isn't an authentication failure is passed straight through.
      if (err.status !== 401) {
        return throwError(err);
      }
 
      // Start a token refresh if one isn't already running; otherwise reuse it.
      if (refreshInFlight$ == null) {
        refreshInFlight$ = authService.refreshToken().pipe(
          finalize(() => { refreshInFlight$ = null; }),
          shareReplay(1)
        );
      }
 
      // Once the refresh resolves, either replay the original request with the
      // new token or, if the refresh failed, sign the user out.
      return refreshInFlight$.pipe(
        switchMap((result: LoginResult) => {
          if (!result.success) {
            authService.logout();
            return throwError(err);
          }
 
          // Replay the original request. If it still comes back unauthorized,
          // the refreshed token isn't valid, so end the session.
          return next(addHeaders(req, authService)).pipe(
            catchError((retryErr: HttpErrorResponse) => {
              if (retryErr.status === 401) {
                authService.logout();
              }
              return throwError(retryErr);
            })
          );
        })
      );
    })
  );
};